import { CoreLayoutView, ORIENTATION_ENUM, TCoreLayoutStyleProps } from '@core/widgets';
import React from 'react';
import { THorizontalLayoutOption } from '../../types/options';
import { getBackgroundHref, resolveCssColor } from '../Image/imageUtils';

const toLine = (value?: string): TCoreLayoutStyleProps['lineX'] => (value ? (value.replace(/-/g, '_') as TCoreLayoutStyleProps['lineX']) : undefined);

export type THorizontalLayoutViewProps = {
    /** Value for the `data-test` attribute (widget uid in server-driven scenarios). */
    uid?: string;
    /** Pre-resolved visibility state (derived from useBehavior one level up). */
    isHide?: boolean;
    /** Vertical alignment of children, mapped to CoreLayout `lineY`. */
    alignment?: THorizontalLayoutOption['alignment'];
    /** Horizontal arrangement of children, mapped to CoreLayout `lineX`. */
    arrangement?: THorizontalLayoutOption['arrangement'];
    /** Server-driven theme style name forwarded to CoreLayoutView for theme resolution. */
    styleName?: string;
    children?: React.ReactNode;
    /** Design version of the owning widget: a semantic `backgroundColor` resolves to that version's token. */
    version: number | undefined;
} & Pick<THorizontalLayoutOption, 'margin' | 'backgroundColor' | 'backgroundImage'>;

/**
 * Composable horizontal layout building block for use inside complex widgets.
 * Owns only rendering logic — the mapping of alignment/arrangement options into
 * CoreLayout style props and delegation to CoreLayoutView.
 *
 * Accepts pre-resolved behavior props (isHide) and plain display values from
 * the parent. It NEVER receives a `widget` object — every value is an explicit
 * prop, resolved one level up (see HorizontalLayoutWidget).
 */
const HorizontalLayoutView = ({
    uid,
    isHide,
    alignment = 'start',
    arrangement = 'center',
    styleName,
    margin,
    backgroundColor,
    backgroundImage,
    children,
    version,
}: THorizontalLayoutViewProps) => {
    const styleProps: TCoreLayoutStyleProps = {
        orientation: ORIENTATION_ENUM.HORIZONTAL,
        lineX: toLine(arrangement),
        lineY: alignment as TCoreLayoutStyleProps['lineY'],
        backgroundColor: resolveCssColor(backgroundColor, version),
        backgroundImage: getBackgroundHref(backgroundImage),
        margin,
    };

    return (
        <CoreLayoutView uid={uid} styleProps={styleProps} isHide={isHide} styleName={styleName}>
            {children}
        </CoreLayoutView>
    );
};

export { HorizontalLayoutView };
